import { useStore } from '@nanostores/react';
import { motion } from 'framer-motion';
import { memo, useEffect, useRef, useState } from 'react';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';

const TRANSITION = { duration: 0.2, ease: cubicEasingFn };

interface PromptInspectedElementProps {
  hasAttachments: boolean;
}

const clear = () => {
  workbenchStore.setInspectedElement(null);
};

export const PromptInspectedElement = memo<PromptInspectedElementProps>(({ hasAttachments }) => {
  const contentDiv = useRef<HTMLDivElement>(null);

  const inspectedElement = useStore(workbenchStore.inspectedElement);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentDiv.current) {
      setContentHeight(contentDiv.current.getBoundingClientRect().height);
    }
  }, []);

  return (
    <motion.div
      transition={TRANSITION}
      animate={{ height: inspectedElement ? contentHeight : 0 }}
      initial={false}
      className={classNames({
        'border-b-px': Boolean(inspectedElement),
        'bg-bolt-elements-background-depth-1': hasAttachments,
        'border-bolt-elements-borderColor': hasAttachments,
        'border-transparent': !hasAttachments,
        relative: hasAttachments && Boolean(inspectedElement),
      })}
    >
      <div
        className={classNames(
          'overflow-hidden h-full border-bolt-elements-borderColor relative bg-bolt-elements-background-depth-2 transition-opacity duration-200',
          {
            'rounded-t-[0.44rem]': true,
            'border-px -left-px w-[calc(100%+2px)] h-[calc(100%+1px)]': hasAttachments,
            'border-b-px left-0 right-0': !hasAttachments,
            'opacity-0': !inspectedElement,
            'opacity-100': Boolean(inspectedElement),
          },
        )}
      >
        <div ref={contentDiv} className="flex py-2.5 px-2.5 font-medium text-xs">
          <div className="flex justify-between items-center w-full">
            <div>
              {inspectedElement ? (
                <>
                  <code className="bg-accent-500 text-white rounded-4px px-1.5 py-1 mr-0.5">{`${inspectedElement.tagName}`}</code>{' '}
                  <span>selected for inspection</span>
                </>
              ) : null}
            </div>
            <button className="bg-transparent text-bolt-elements-link hover:underline" onClick={clear}>
              Clear
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
});
